 declare global {
  interface Window {
    mobileHooks?: {
      /**
       * Override this function in components when they are on top of the layer stack
       * to change the way the native application's back button will behave. 
       * One common use-case is making a layer (eg dialog, full screen filter) close
       * when the app's back button is pressed.
       * @return true if the web application has handled the back button press,
       * false if the web application did not take any action and the native application
       * should take action instead.
      */
        hybridGoBack?: () => boolean;

      /**
       * Other hooks may be set, but we don't know their types here
      */
      [key: string]: (...args:unknown[]) => unknown; 
    };
  }
 }

/**
 * This module provides low-level functions for setting up JS callbacks for mobile app
 * webviews to run. Before using these, check if a method on {@link MobileHooks}
 * meets your needs.
 */
export class NativeToWebMessaging {

  /**
   * Opt into using mobile hooks in an application by calling this function.
   * If an application does not call this, `setMobileHook` will fail gracefully,
   * preventing unnecessary functions from being exposed on `window.mobileHooks`
   * by components in various libraries.
   */
  static initializeMobileHooks() {
    window.mobileHooks = {}
  }

  /**
   * Add `hookFn` function to `window.mobileHooks` if it exists.
   * Native applications will be able to run `hookFn` via their webviews.
   * If a `hookFn` is already set with this `name`, it will be overwritten by the new `hookFn`.
   * @param name - the unique name to expose this callback as on the `window.mobileHooks` object.
   * @param hookFn - the callback to run when the native application wants to take an action
   * 
   * @example
   * ```
   * NativeToWebMessaging.setMobileHook('save-draft', saveDraft)
   * ```
   */
  static setMobileHook (name: keyof Window["mobileHooks"], hookFn: (...args: unknown[]) => unknown) {  
    
    if (!window.mobileHooks) {
      console.warn(`window.mobileHooks does not exist. ${name} will not be registered.`)
      return 
    }

    window.mobileHooks[name] = hookFn;
  }
}
